import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import defaultImg from '../../asset/project/default_1.png';
import deleteIcon from '../../asset/icon/delete.png';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { CarouselItemData } from '../../shared/dto/EntityRepository';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';
import {
  ImportStationSelectedData,
  useImportStationWorkStore,
} from '../../shared/store/importStationWorkStore';
import {
  CAROUSEL_MAX_ITEMS,
  getFilledCarouselItems,
  resolveCarouselSlotImage,
} from '../../shared/util/carouselUtils';

const PREVIEW_SIZE = 72;

interface CarouselImportSelectionSlotsProps {
  items: CarouselItemData[];
  replaceSlotIndex?: number | null;
}

const ExistingSlide: React.FC<{
  item: CarouselItemData;
  allowRemove: boolean;
  isReplacing: boolean;
}> = ({ item, allowRemove, isReplacing }) => {
  const imageRef = useRef<HTMLDivElement>(null);
  const [isHover, setIsHover] = useState(false);
  const addRemovedCarouselItemId = useImportStationWorkStore((s) => s.addRemovedCarouselItemId);
  const image = resolveCarouselSlotImage(item);
  const imageUrl = useOptimizedImageUrl(image?.originUrl ?? '', imageRef);
  const handleRemove = (event: React.MouseEvent) => {
    event.stopPropagation();
    addRemovedCarouselItemId(String(item.id));
  };

  return (
    <Preview
      ref={imageRef}
      $dimmed={!allowRemove && !isReplacing}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <PreviewImage
        $isHover={allowRemove && isHover}
        src={imageUrl || defaultImg}
        alt={image?.alt || `Slide ${item.slotIndex + 1}`}
      />
      {allowRemove && (
        <HoverOverlay $isHover={isHover} onClick={handleRemove}>
          <UniversalIconBtn
            size="fit"
            border={false}
            isTransparent={true}
            backgroundImg={deleteIcon}
            onClick={handleRemove}
            aria-label="Remove slide"
          />
        </HoverOverlay>
      )}
    </Preview>
  );
};

const SelectedSlide: React.FC<{ item: ImportStationSelectedData }> = ({ item }) => {
  const imageRef = useRef<HTMLDivElement>(null);
  const [isHover, setIsHover] = useState(false);
  const removeSelectedData = useImportStationWorkStore((s) => s.removeSelectedData);
  const imageUrl = useOptimizedImageUrl(item.originUrl, imageRef);
  const handleRemove = (event: React.MouseEvent) => {
    event.stopPropagation();
    removeSelectedData(item);
  };

  return (
    <Preview
      ref={imageRef}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <PreviewImage
        $isHover={isHover}
        src={imageUrl || defaultImg}
        alt={item.sourceDetail?.description || item.title || 'Selected slide'}
      />
      <HoverOverlay $isHover={isHover} onClick={handleRemove}>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent={true}
          backgroundImg={deleteIcon}
          onClick={handleRemove}
          aria-label="Remove selected slide"
        />
      </HoverOverlay>
    </Preview>
  );
};

const CarouselImportSelectionSlots: React.FC<CarouselImportSelectionSlotsProps> = ({
  items,
  replaceSlotIndex = null,
}) => {
  const selectedDataList = useImportStationWorkStore((s) => s.selectedDataList);
  const removedCarouselItemIds = useImportStationWorkStore((s) => s.removedCarouselItemIds);
  const removedIds = new Set(removedCarouselItemIds);
  const filledItems = getFilledCarouselItems(items).filter((item) => !removedIds.has(String(item.id)));
  const emptyCount = Math.max(
    0,
    CAROUSEL_MAX_ITEMS - filledItems.length - selectedDataList.length,
  );

  return (
    <Slots aria-label="Carousel import selections">
      {filledItems.map((item) => (
        <ExistingSlide
          key={item.id}
          item={item}
          allowRemove={replaceSlotIndex == null}
          isReplacing={item.slotIndex === replaceSlotIndex}
        />
      ))}
      {replaceSlotIndex == null && selectedDataList.map((item) => (
        <SelectedSlide
          key={`${item.workId}-${item.detailId ?? 'work'}`}
          item={item}
        />
      ))}
      {Array.from({ length: emptyCount }, (_, index) => (
        <EmptySlot key={`empty-${index}`} aria-label="Empty slide">+</EmptySlot>
      ))}
    </Slots>
  );
};

const Slots = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
  overflow-x: auto;
  border-top: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Preview = styled.div<{ $dimmed?: boolean }>`
  position: relative;
  flex: 0 0 ${PREVIEW_SIZE}px;
  width: ${PREVIEW_SIZE}px;
  height: ${PREVIEW_SIZE}px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.color_Gray_06};
  opacity: ${({ $dimmed }) => ($dimmed ? 0.4 : 1)};
`;

const PreviewImage = styled.img<{ $isHover?: boolean }>`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  filter: ${({ $isHover }) => ($isHover ? 'blur(2px)' : 'none')};
  transition: filter 0.2s ease;
`;

const HoverOverlay = styled.div<{ $isHover: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $isHover }) => ($isHover ? 1 : 0)};
  transition: opacity 0.2s ease;
  cursor: pointer;
`;

const EmptySlot = styled.div`
  flex: 0 0 ${PREVIEW_SIZE}px;
  width: ${PREVIEW_SIZE}px;
  height: ${PREVIEW_SIZE}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${({ theme }) => theme.colors.color_Gray_05};
  background-color: ${({ theme }) => theme.colors.color_Gray_06};
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_03_2};
`;

export default CarouselImportSelectionSlots;
